import type {
  BankDetailsFormData,
  NomineeFormData,
  ProposalFormData,
  ProposerFormData,
} from '@/context/HealthInsuranceFormsContext';
import { RHI_ROUTES } from '@/constants/rhiRoutes';
import { ISelectedMember, MemberFormData, QuoteType } from '@/types';
import { getUserScopedKey } from '@/utils';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

const RIDER_SUPPORTED_INSURERS = ['il', 'icici', 'hdfc'] as const;

export type RhiStep =
  | 'home'
  | 'select-member'
  | 'plans-benefits'
  | 'recommendation'
  | 'add-riders'
  | 'verify-details'
  | 'add-nominee'
  | 'member-details'
  | 'member-details-form'
  | 'checkout'
  | 'post-payment'
  | 'policy-status'
  | 'payment-failed'
  | 'thank-you'
  | 'error';

export interface RhiFlowContext {
  leadId: string;
  selectedPlanId: string;
  selectedQuoteId: string;
  proposalId: string;
}

export interface RhiRouteParams {
  planId?: string;
  relation?: string;
  status?: string;
}

interface StepDefinition {
  prerequisites: (ctx: RhiFlowContext) => boolean;
  next: (ctx: RhiFlowContext) => RhiStep;
  back: (ctx: RhiFlowContext) => RhiStep;
  isResumeCandidate?: boolean;
}

export const DEFAULT_SUM_INSURED = 1_000_000;
export const DEFAULT_SELECTED_MEMBERS: ISelectedMember[] = [];

export type RhiPersistedProposerFormData = Omit<
  ProposerFormData,
  'aadharNumber' | 'panNumber'
>;

export interface RhiFormSnapshots {
  proposal?: ProposalFormData;
  proposer?: RhiPersistedProposerFormData;
  nominee?: NomineeFormData;
  bankDetails?: BankDetailsFormData;
  selectMember?: MemberFormData;
}

export type RhiFormSnapshotKey = keyof RhiFormSnapshots;

export interface RhiFlowPersistedState {
  currentStep: RhiStep;
  leadId: string;
  benefitId: string;
  selectedPlanId: string;
  selectedMembers: ISelectedMember[];
  selectedSumInsured: number;
  essentialRidersIncluded: boolean;
  includedRiders: string[];
  proposalId: string;
  quoteSnapshot: QuoteType[];
  formSnapshots: RhiFormSnapshots;
}

export interface RhiFlowHydrateInput extends Partial<RhiFlowPersistedState> {}

interface RhiFlowStore extends RhiFlowPersistedState {
  setLeadId: (id: string) => void;
  setBenefitId: (id: string) => void;
  setSelectedPlanId: (id: string) => void;
  setSelectedMembers: (members: ISelectedMember[]) => void;
  setSelectedSumInsured: (amount: number) => void;
  setEssentialRidersIncluded: (included: boolean) => void;
  setIncludedRiders: (riders: string[]) => void;
  setProposalId: (id: string) => void;
  setQuoteSnapshot: (quotes: QuoteType[]) => void;
  setFormSnapshot: <K extends RhiFormSnapshotKey>(
    form: K,
    data: RhiFormSnapshots[K],
  ) => void;
  next: () => RhiStep;
  back: () => RhiStep;
  goTo: (step: RhiStep) => RhiStep;
  resume: (fromStep?: RhiStep) => RhiStep;
  canBeAt: (step: RhiStep) => boolean;
  hydrate: (data: RhiFlowHydrateInput) => void;
  reset: () => void;
}

export const RHI_FLOW_STORAGE_KEY = 'rhi-flow';

export const getRhiFlowPersistKey = () => getUserScopedKey(RHI_FLOW_STORAGE_KEY);

const createInitialPersistedState = (): RhiFlowPersistedState => ({
  currentStep: 'home',
  leadId: '',
  benefitId: '',
  selectedPlanId: '',
  selectedMembers: DEFAULT_SELECTED_MEMBERS,
  selectedSumInsured: DEFAULT_SUM_INSURED,
  essentialRidersIncluded: false,
  includedRiders: [],
  proposalId: '',
  quoteSnapshot: [],
  formSnapshots: {},
});

export const RHI_STEP_ORDER: RhiStep[] = [
  'home',
  'select-member',
  'plans-benefits',
  'recommendation',
  'add-riders',
  'verify-details',
  'add-nominee',
  'member-details',
  'member-details-form',
  'checkout',
];

const alwaysAllowed = () => true;

const hasLead = ({ leadId }: RhiFlowContext) => !!leadId;
const hasSelectedPlan = ({ leadId, selectedPlanId }: RhiFlowContext) =>
  !!leadId && !!selectedPlanId;
const hasSelectedQuote = ({ selectedQuoteId }: RhiFlowContext) =>
  !!selectedQuoteId;
const hasProposal = ({ proposalId }: RhiFlowContext) => !!proposalId;

export const planSupportsRiders = (planId?: string) => {
  if (!planId) return false;

  const tokens = planId.toLowerCase().split('-'); // ['rhi','il','benefitid']
  return RIDER_SUPPORTED_INSURERS.some((insurer) => tokens.includes(insurer));
};

const terminalBack = () => 'home' as RhiStep;

export const RHI_STEP_DEFINITIONS: Record<RhiStep, StepDefinition> = {
  home: {
    prerequisites: alwaysAllowed,
    next: () => 'select-member',
    back: terminalBack,
  },
  'select-member': {
    prerequisites: alwaysAllowed,
    next: () => 'plans-benefits',
    back: () => 'home',
  },
  'plans-benefits': {
    prerequisites: hasLead,
    next: () => 'recommendation',
    back: () => 'select-member',
  },
  recommendation: {
    prerequisites: hasLead,
    next: ({ selectedPlanId }) =>
      planSupportsRiders(selectedPlanId) ? 'add-riders' : 'verify-details',
    back: () => 'plans-benefits',
  },
  'add-riders': {
    prerequisites: hasSelectedPlan,
    next: () => 'verify-details',
    back: () => 'recommendation',
  },
  'verify-details': {
    prerequisites: hasSelectedQuote,
    next: () => 'add-nominee',
    back: ({ selectedPlanId }) =>
      planSupportsRiders(selectedPlanId) ? 'add-riders' : 'recommendation',
  },
  'add-nominee': {
    prerequisites: hasSelectedQuote,
    next: () => 'member-details',
    back: () => 'verify-details',
  },
  'member-details': {
    prerequisites: hasSelectedQuote,
    next: () => 'checkout',
    back: () => 'add-nominee',
  },
  'member-details-form': {
    prerequisites: hasSelectedQuote,
    next: () => 'member-details',
    back: () => 'member-details',
    isResumeCandidate: false,
  },
  checkout: {
    prerequisites: hasProposal,
    next: () => 'post-payment',
    back: () => 'member-details',
  },
  'post-payment': {
    prerequisites: alwaysAllowed,
    next: () => 'post-payment',
    back: terminalBack,
    isResumeCandidate: false,
  },
  'policy-status': {
    prerequisites: alwaysAllowed,
    next: () => 'policy-status',
    back: terminalBack,
    isResumeCandidate: false,
  },
  'payment-failed': {
    prerequisites: alwaysAllowed,
    next: () => 'payment-failed',
    back: terminalBack,
    isResumeCandidate: false,
  },
  'thank-you': {
    prerequisites: alwaysAllowed,
    next: () => 'thank-you',
    back: terminalBack,
    isResumeCandidate: false,
  },
  error: {
    prerequisites: alwaysAllowed,
    next: () => 'error',
    back: terminalBack,
    isResumeCandidate: false,
  },
};

export const resolveRhiRoute = (
  step: RhiStep,
  params: RhiRouteParams = {},
): string => {
  switch (step) {
    case 'home':
      return RHI_ROUTES.HOME;
    case 'select-member':
      return RHI_ROUTES.SELECT_MEMBER;
    case 'plans-benefits':
      return RHI_ROUTES.PLANS_BENEFITS;
    case 'recommendation':
      return RHI_ROUTES.RECOMMENDATION;
    case 'add-riders':
      return RHI_ROUTES.ADD_RIDERS(params.planId ?? '');
    case 'verify-details':
      return RHI_ROUTES.VERIFY_DETAILS;
    case 'add-nominee':
      return RHI_ROUTES.ADD_NOMINEE;
    case 'member-details':
      return RHI_ROUTES.MEMBER_DETAILS;
    case 'member-details-form':
      return params.relation
        ? RHI_ROUTES.MEMBER_DETAILS_RELATION(params.relation)
        : RHI_ROUTES.MEMBER_DETAILS;
    case 'checkout':
      return RHI_ROUTES.CHECKOUT;
    case 'post-payment':
      return RHI_ROUTES.POST_PAYMENT;
    case 'policy-status':
      return RHI_ROUTES.POLICY_STATUS(params.status ?? '');
    case 'payment-failed':
      return RHI_ROUTES.PAYMENT_FAILED;
    case 'thank-you':
      return RHI_ROUTES.THANK_YOU;
    case 'error':
      return RHI_ROUTES.ERROR;
    default:
      return RHI_ROUTES.HOME;
  }
};

export const getRhiStepFromPathname = (pathname: string): RhiStep | null => {
  const normalizedPathname = pathname.replace(/\/$/, '');
  const basePath = RHI_ROUTES.HOME;

  if (normalizedPathname === basePath) return 'home';
  if (normalizedPathname === RHI_ROUTES.SELECT_MEMBER) return 'select-member';
  if (normalizedPathname === RHI_ROUTES.PLANS_BENEFITS) return 'plans-benefits';
  if (normalizedPathname === RHI_ROUTES.RECOMMENDATION) return 'recommendation';
  if (normalizedPathname.startsWith(`${basePath}/add-riders/`)) {
    return 'add-riders';
  }
  if (normalizedPathname === RHI_ROUTES.VERIFY_DETAILS) return 'verify-details';
  if (normalizedPathname === RHI_ROUTES.ADD_NOMINEE) return 'add-nominee';
  if (normalizedPathname === RHI_ROUTES.MEMBER_DETAILS) return 'member-details';
  if (normalizedPathname.startsWith(`${basePath}/member-details/`)) {
    return 'member-details-form';
  }
  if (normalizedPathname === RHI_ROUTES.CHECKOUT) return 'checkout';
  if (normalizedPathname === RHI_ROUTES.POST_PAYMENT) return 'post-payment';
  if (normalizedPathname === RHI_ROUTES.PAYMENT_FAILED) return 'payment-failed';
  if (normalizedPathname === RHI_ROUTES.THANK_YOU) return 'thank-you';
  if (normalizedPathname === RHI_ROUTES.ERROR) return 'error';
  if (normalizedPathname === `${basePath}/policy-status`) return 'policy-status';
  if (normalizedPathname.startsWith(`${basePath}/policy-status/`)) {
    return 'policy-status';
  }

  return null;
};

export const getRhiRouteParamsFromPathname = (
  pathname: string,
): RhiRouteParams => {
  const normalizedPathname = pathname.replace(/\/$/, '');
  const basePath = RHI_ROUTES.HOME;

  if (normalizedPathname.startsWith(`${basePath}/add-riders/`)) {
    return {
      planId: normalizedPathname.replace(`${basePath}/add-riders/`, ''),
    };
  }

  if (normalizedPathname.startsWith(`${basePath}/member-details/`)) {
    return {
      relation: normalizedPathname.replace(`${basePath}/member-details/`, ''),
    };
  }

  if (normalizedPathname.startsWith(`${basePath}/policy-status/`)) {
    return {
      status: normalizedPathname.replace(`${basePath}/policy-status/`, ''),
    };
  }

  return {};
};

const getDefinition = (step: RhiStep) => RHI_STEP_DEFINITIONS[step];

export const deriveFlowContext = (
  state: RhiFlowPersistedState,
): RhiFlowContext => {
  const selectedQuote = state.selectedPlanId
    ? state.quoteSnapshot.find((q) => q.planId === state.selectedPlanId)
    : state.quoteSnapshot[0];

  return {
    leadId: state.leadId,
    selectedPlanId: state.selectedPlanId,
    selectedQuoteId: selectedQuote?.quoteId ?? '',
    proposalId: state.proposalId,
  };
};

const canBeAtStep = (step: RhiStep, ctx: RhiFlowContext) =>
  getDefinition(step).prerequisites(ctx);

const findLatestValidStep = (
  ctx: RhiFlowContext,
  fromStep: RhiStep = 'checkout',
) => {
  const requestedIndex = RHI_STEP_ORDER.indexOf(fromStep);
  const startIndex =
    requestedIndex === -1 ? RHI_STEP_ORDER.length - 1 : requestedIndex;

  for (let index = startIndex; index >= 0; index -= 1) {
    const step = RHI_STEP_ORDER[index];
    const definition = getDefinition(step);

    if (definition.isResumeCandidate === false) continue;
    if (definition.prerequisites(ctx)) return step;
  }

  return 'home';
};

export const useRhiFlowStore = create<RhiFlowStore>()(
  persist(
    (set, get) => ({
      ...createInitialPersistedState(),
      setLeadId: (leadId) => set({ leadId }),
      setBenefitId: (benefitId) => set({ benefitId }),
      setSelectedPlanId: (selectedPlanId) => set({ selectedPlanId }),
      setSelectedMembers: (selectedMembers) => set({ selectedMembers }),
      setSelectedSumInsured: (selectedSumInsured) => set({ selectedSumInsured }),
      setEssentialRidersIncluded: (essentialRidersIncluded) =>
        set({ essentialRidersIncluded }),
      setIncludedRiders: (includedRiders) => set({ includedRiders }),
      setProposalId: (proposalId) => set({ proposalId }),
      setQuoteSnapshot: (quoteSnapshot) => set({ quoteSnapshot }),
      setFormSnapshot: (form, data) => {
        set((state) => ({
          formSnapshots: {
            ...state.formSnapshots,
            [form]: data,
          },
        }));
      },
      goTo: (step) => {
        const ctx = deriveFlowContext(get());
        if (!canBeAtStep(step, ctx)) {
          set({ currentStep: 'error' });
          return 'error';
        }
        set({ currentStep: step });
        return step;
      },
      next: () => {
        const ctx = deriveFlowContext(get());
        const target = getDefinition(get().currentStep).next(ctx);
        return get().goTo(target);
      },
      back: () => {
        const ctx = deriveFlowContext(get());
        const target = getDefinition(get().currentStep).back(ctx);
        return get().goTo(target);
      },
      resume: (fromStep) => {
        const ctx = deriveFlowContext(get());
        const target = findLatestValidStep(ctx, fromStep);
        set({ currentStep: target });
        return target;
      },
      canBeAt: (step) => {
        const ctx = deriveFlowContext(get());
        return canBeAtStep(step, ctx);
      },
      hydrate: (data) => {
        set((state) => ({
          ...state,
          ...data,
          formSnapshots: data.formSnapshots
            ? { ...state.formSnapshots, ...data.formSnapshots }
            : state.formSnapshots,
        }));
      },
      reset: () => {
        set(createInitialPersistedState());
      },
    }),
    {
      name: getRhiFlowPersistKey(),
      partialize: (state): RhiFlowPersistedState => ({
        currentStep: state.currentStep,
        leadId: state.leadId,
        benefitId: state.benefitId,
        selectedPlanId: state.selectedPlanId,
        selectedMembers: state.selectedMembers,
        selectedSumInsured: state.selectedSumInsured,
        essentialRidersIncluded: state.essentialRidersIncluded,
        includedRiders: state.includedRiders,
        proposalId: state.proposalId,
        quoteSnapshot: state.quoteSnapshot,
        formSnapshots: state.formSnapshots,
      }),
      storage: createJSONStorage(() => window.sessionStorage),
    },
  ),
);
